import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  AlertTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Snackbar,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Cloud as CloudIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Lock as LockIcon,
  Speed as SpeedIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material'

const EnterpriseAccessPage = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  // GCC Tax Laws color scheme
  const colors = {
    primary: '#232536',
    secondary: '#ffcf51',
    success: '#43af76',
    error: '#e74c3c',
    warning: '#ff9800',
    info: '#1976d2',
    lightBg: '#f5f7fa',
    cardBg: '#ffffff',
  }

  // Document type colors from your system
  const categoryColors = {
    Business: { main: '#1976d2', light: '#e3f2fd' },
    'Legal Research': { main: '#2e7d32', light: '#e8f5e9' },
    Security: { main: '#43af76', light: '#e8f5e9' },
    Network: { main: '#673ab7', light: '#ede7f6' },
  }

  const whitelistTemplate = `To: [Your IT Security Team]
Subject: Business Application Whitelist Request - gcctaxlaws.com

Dear IT Security Team,

I am requesting approval to whitelist the following website currently being 
blocked by our security appliance:

DOMAIN INFORMATION:
URL: https://gcctaxlaws.com
Current Status: Blocked by FortiGuard
Category: Newly Observed Domain
SSL Labs Grade: A (Verified)
Requested Action: Add to whitelist/allowed domains

BUSINESS JUSTIFICATION:
This website is essential for professional duties. GCC Tax Laws is a 
legitimate legal research platform providing:
- Tax legislation for all GCC countries
- VAT implementation guidelines
- Corporate tax regulations
- Compliance frameworks
- Official government regulatory documents

PLATFORM LEGITIMACY:
✓ Operated by Claritax Technology Solutions LLC FZ
✓ 500+ professional users across GCC
✓ Valid SSL certificate (Grade A)
✓ No security risks or malicious content

REQUEST:
Please add gcctaxlaws.com to approved/whitelisted domains for 
business-critical legal research purposes.

Contact: [Your Name], [Your Department], [Your Email]`

  const handleCopyTemplate = () => {
    navigator.clipboard.writeText(whitelistTemplate)
    setSnackbar({ open: true, message: 'Template copied to clipboard!' })
  }

  const handleDownloadTemplate = () => {
    const blob = new Blob([whitelistTemplate], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'gcctaxlaws_whitelist_request.txt'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
    setSnackbar({ open: true, message: 'Template downloaded!' })
  }

  return (
    <Box sx={{ bgcolor: colors.lightBg, minHeight: '100vh', py: 4 }}>
      <Container maxWidth='lg'>
        {/* Hero Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h3'
            sx={{
              fontWeight: 700,
              color: colors.primary,
              mb: 2,
              fontSize: { xs: '1.75rem', md: '2.5rem' },
            }}
          >
            Enterprise Access Information
          </Typography>
          <Typography
            variant='h6'
            sx={{
              color: '#666',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Network Configuration and Firewall Whitelist Guidelines for IT
            Departments
          </Typography>
        </Box>

        {/* Quick Actions */}
        <Card sx={{ mb: 4, bgcolor: colors.cardBg, boxShadow: 3 }}>
          <CardContent>
            <Typography
              variant='h5'
              sx={{ fontWeight: 600, color: colors.primary, mb: 3 }}
            >
              Quick Actions
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
              }}
            >
              <Button
                variant='contained'
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyTemplate}
                sx={{
                  bgcolor: colors.primary,
                  '&:hover': { bgcolor: '#3a3e5a' },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Copy Whitelist Template
              </Button>
              <Button
                variant='contained'
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                sx={{
                  bgcolor: colors.secondary,
                  color: colors.primary,
                  '&:hover': { bgcolor: '#f5c541' },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Download Template
              </Button>
              <Button
                variant='outlined'
                startIcon={<OpenInNewIcon />}
                href='https://www.ssllabs.com/ssltest/analyze.html?d=gcctaxlaws.com'
                target='_blank'
                sx={{
                  borderColor: colors.primary,
                  color: colors.primary,
                  '&:hover': {
                    borderColor: '#3a3e5a',
                    bgcolor: 'rgba(35, 37, 54, 0.04)',
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Verify SSL Certificate
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Current Issue Alert */}
        <Alert
          severity='warning'
          icon={<WarningIcon />}
          sx={{ mb: 4, boxShadow: 2 }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            Common Issue: FortiGuard "Newly Observed Domain" Block
          </AlertTitle>
          <Typography variant='body2' sx={{ mb: 1 }}>
            If your organization uses Fortinet security appliances and employees
            cannot access gcctaxlaws.com, this is typically due to our domain
            being categorized as "Newly Observed." We have submitted a
            recategorization request to FortiGuard.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label='Recategorization Pending'
              size='small'
              sx={{ bgcolor: '#fff3cd', color: '#856404', fontWeight: 600 }}
            />
            <Chip
              label='SSL Grade: A'
              size='small'
              sx={{ bgcolor: '#d4edda', color: '#155724', fontWeight: 600 }}
            />
          </Box>
          <Typography variant='body2' sx={{ mt: 2, fontWeight: 600 }}>
            Temporary Solution: IT departments can manually whitelist
            gcctaxlaws.com using the template above.
          </Typography>
        </Alert>

        {/* Platform Information */}
        <Card sx={{ mb: 4, bgcolor: colors.cardBg, boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BusinessIcon
                sx={{ fontSize: 32, color: colors.primary, mr: 2 }}
              />
              <Typography
                variant='h5'
                sx={{ fontWeight: 600, color: colors.primary }}
              >
                Platform Information
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: colors.lightBg, height: '100%' }}>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: colors.primary, mb: 2 }}
                  >
                    Platform Details
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary='Platform'
                        secondary='GCC Tax Laws'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='Company'
                        secondary='Claritax Technology Solutions LLC FZ'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='Domain'
                        secondary='gcctaxlaws.com'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='Type'
                        secondary='Legal Research Platform'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='Users'
                        secondary='500+ users across the globe'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: colors.lightBg, height: '100%' }}>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: colors.primary, mb: 2 }}
                  >
                    Security Status
                  </Typography>
                  <List dense>
                    {[
                      'Valid SSL/TLS Certificate',
                      'HTTPS Encryption',
                      'No Malware/Malicious Content',
                      'Professional Content Only',
                      'Privacy Policy Compliant',
                    ].map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon
                            sx={{ color: colors.success, fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <Alert severity='info' icon={<DescriptionIcon />} sx={{ mt: 3 }}>
              <Typography variant='body2'>
                <strong>Business Purpose:</strong> GCC Tax Laws provides tax
                legislation, regulatory documents, and compliance materials for
                UAE, KSA, Kuwait, Qatar, Oman, and Bahrain. Our users include
                tax consultants, legal professionals, accountants, and
                compliance officers at major financial institutions.
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Network Requirements */}
        <Card sx={{ mb: 4, bgcolor: colors.cardBg, boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CloudIcon sx={{ fontSize: 32, color: colors.primary, mr: 2 }} />
              <Typography
                variant='h5'
                sx={{ fontWeight: 600, color: colors.primary }}
              >
                Network Requirements
              </Typography>
            </Box>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, color: colors.primary, mb: 2, mt: 3 }}
            >
              Domain Whitelisting
            </Typography>
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead sx={{ bgcolor: colors.lightBg }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                    <TableCell align='center' sx={{ fontWeight: 600 }}>
                      Required
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>gcctaxlaws.com</TableCell>
                    <TableCell>Main application domain</TableCell>
                    <TableCell align='center'>
                      <CheckCircleIcon sx={{ color: colors.success }} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>*.gcctaxlaws.com</TableCell>
                    <TableCell>Subdomains (if applicable)</TableCell>
                    <TableCell align='center'>
                      <Chip label='Optional' size='small' />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, color: colors.primary, mb: 2 }}
            >
              Port Requirements
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <LockIcon sx={{ color: colors.success }} />
                </ListItemIcon>
                <ListItemText
                  primary='Port 443 (HTTPS)'
                  secondary='Required for secure access'
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SpeedIcon sx={{ color: colors.info }} />
                </ListItemIcon>
                <ListItemText
                  primary='Port 80 (HTTP)'
                  secondary='Redirects to HTTPS'
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            </List>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, color: colors.primary, mb: 2, mt: 2 }}
            >
              SSL/TLS Configuration
            </Typography>
            <Paper sx={{ p: 2, bgcolor: colors.lightBg }}>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary='Supported Protocols'
                    secondary='TLS 1.2, TLS 1.3'
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary='Certificate Authority'
                    secondary="Let's Encrypt (R12)"
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary='Certificate Status'
                    secondary='Valid and automatically renewed'
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary='SSL Labs Grade'
                    secondary='A (Excellent)'
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItem>
              </List>
            </Paper>
          </CardContent>
        </Card>

        {/* Firewall Configuration Guides */}
        <Card sx={{ mb: 4, bgcolor: colors.cardBg, boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SettingsIcon
                sx={{ fontSize: 32, color: colors.primary, mr: 2 }}
              />
              <Typography
                variant='h5'
                sx={{ fontWeight: 600, color: colors.primary }}
              >
                Firewall Configuration Guides
              </Typography>
            </Box>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  FortiGate / FortiGuard
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemText primary='1. Navigate to Security Profiles → Web Filter' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='2. Edit your active Web Filter profile' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='3. Go to Static URL Filter' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='4. Add gcctaxlaws.com with action "Exempt"' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='5. Apply and save changes' />
                  </ListItem>
                </List>
                <Alert severity='success' sx={{ mt: 2 }}>
                  <AlertTitle>Note</AlertTitle>
                  FortiGuard recategorization is pending. Once approved, manual
                  whitelist may not be necessary.
                </Alert>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Cisco Umbrella</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemText primary='1. Log in to Cisco Umbrella Dashboard' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='2. Navigate to Policies → Policy Components → Destination Lists' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='3. Create or edit a destination list' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='4. Add gcctaxlaws.com to the allow list' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='5. Apply to relevant policy' />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Palo Alto Networks
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItem>
                    <ListItemText primary='1. Navigate to Objects → Custom Objects → URL Category' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='2. Create new URL category (e.g., "Business-Legal-Research")' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='3. Add gcctaxlaws.com to the list' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='4. Update security policy to allow this category' />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary='5. Commit changes' />
                  </ListItem>
                </List>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Proxy/PAC File Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant='body2' sx={{ mb: 2 }}>
                  If using proxy auto-config (PAC) files, add exception:
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: '#1e1e1e',
                    color: '#d4d4d4',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                  }}
                >
                  <pre style={{ margin: 0 }}>
                    {`if (shExpMatch(host, "*.gcctaxlaws.com") || 
    shExpMatch(host, "gcctaxlaws.com")) {
    return "DIRECT";
}`}
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>

        {/* Security Verification */}
        <Card sx={{ mb: 4, bgcolor: colors.cardBg, boxShadow: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <SecurityIcon
                sx={{ fontSize: 32, color: colors.primary, mr: 2 }}
              />
              <Typography
                variant='h5'
                sx={{ fontWeight: 600, color: colors.primary }}
              >
                Security Verification
              </Typography>
            </Box>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, color: colors.primary, mb: 2 }}
            >
              How to Verify Our Security
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, bgcolor: colors.lightBg, height: '100%' }}>
                  <VerifiedIcon
                    sx={{ fontSize: 48, color: colors.success, mb: 2 }}
                  />
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                    SSL Certificate Check
                  </Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    Verify our SSL configuration and security grade
                  </Typography>
                  <Button
                    variant='outlined'
                    size='small'
                    href='https://www.ssllabs.com/ssltest/analyze.html?d=gcctaxlaws.com'
                    target='_blank'
                    endIcon={<OpenInNewIcon />}
                    sx={{
                      borderColor: colors.primary,
                      color: colors.primary,
                      textTransform: 'none',
                    }}
                  >
                    Check SSL Labs
                  </Button>
                  <Typography
                    variant='caption'
                    display='block'
                    sx={{ mt: 1, color: colors.success }}
                  >
                    Expected Grade: A or A+
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, bgcolor: colors.lightBg, height: '100%' }}>
                  <SecurityIcon
                    sx={{ fontSize: 48, color: colors.info, mb: 2 }}
                  />
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                    Malware Scan
                  </Typography>
                  <Typography variant='body2' sx={{ mb: 2 }}>
                    Scan our domain for threats and malware
                  </Typography>
                  <Button
                    variant='outlined'
                    size='small'
                    href='https://www.virustotal.com/gui/url/ca3b42c89825935d29e132774dc61d93ff588c2e9e35944c74d354be313fadbb'
                    target='_blank'
                    endIcon={<OpenInNewIcon />}
                    sx={{
                      borderColor: colors.primary,
                      color: colors.primary,
                      textTransform: 'none',
                    }}
                  >
                    VirusTotal Scan
                  </Button>
                  <Typography
                    variant='caption'
                    display='block'
                    sx={{ mt: 1, color: colors.success }}
                  >
                    Expected: No threats detected
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3, bgcolor: colors.lightBg, height: '100%' }}>
                  <CheckCircleIcon
                    sx={{ fontSize: 48, color: colors.warning, mb: 2 }}
                  />
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                    Website Safety
                  </Typography>
                  <Typography variant='body2' sx={{ mb: 1 }}>
                    Check with multiple security vendors:
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemText primary='• Google Safe Browsing' />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText primary='• Norton Safe Web' />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText primary='• McAfee SiteAdvisor' />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <Alert severity='info' icon={<SecurityIcon />} sx={{ mt: 3 }}>
              <AlertTitle sx={{ fontWeight: 600 }}>
                For IT Departments
              </AlertTitle>
              We welcome security audits and are happy to provide additional
              documentation including business registration, security policies,
              and data handling procedures upon request.
            </Alert>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card sx={{ mb: 4, bgcolor: colors.cardBg, boxShadow: 3 }}>
          <CardContent>
            <Typography
              variant='h5'
              sx={{ fontWeight: 600, color: colors.primary, mb: 3 }}
            >
              Troubleshooting Common Access Issues
            </Typography>

            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ bgcolor: colors.lightBg }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Issue</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cause</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Solution</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>"Access Blocked" message</TableCell>
                    <TableCell>Firewall/content filter block</TableCell>
                    <TableCell>Submit whitelist request to IT</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>SSL certificate warning</TableCell>
                    <TableCell>Corporate SSL inspection</TableCell>
                    <TableCell>Add to SSL inspection bypass list</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>"Newly Observed Domain"</TableCell>
                    <TableCell>FortiGuard categorization</TableCell>
                    <TableCell>
                      Manual whitelist (pending recategorization)
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Slow loading</TableCell>
                    <TableCell>Proxy/inspection overhead</TableCell>
                    <TableCell>Add to direct connection exceptions</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card sx={{ mb: 4, bgcolor: colors.cardBg, boxShadow: 3 }}>
          <CardContent>
            <Typography
              variant='h5'
              sx={{ fontWeight: 600, color: colors.primary, mb: 3 }}
            >
              Contact Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: colors.lightBg }}>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: colors.primary, mb: 2 }}
                  >
                    Technical Support
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary='Email'
                        secondary='support@gcctaxlaws.com'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='Response Time'
                        secondary='Within 24 hours for IT department inquiries'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: colors.lightBg }}>
                  <Typography
                    variant='h6'
                    sx={{ fontWeight: 600, color: colors.primary, mb: 2 }}
                  >
                    Business Contact
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary='Company'
                        secondary='Claritax Technology Solutions LLC FZ'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary='Website'
                        secondary='gcctaxlaws.com'
                        primaryTypographyProps={{ fontWeight: 600 }}
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
            </Grid>

            <Typography
              variant='h6'
              sx={{ fontWeight: 600, color: colors.primary, mt: 3, mb: 2 }}
            >
              For IT Departments
            </Typography>
            <Paper sx={{ p: 2, bgcolor: colors.lightBg }}>
              <Typography variant='body2' sx={{ mb: 1 }}>
                We provide expedited support for IT departments requiring:
              </Typography>
              <List dense>
                {[
                  'Network configuration assistance',
                  'Security verification documentation',
                  'Business registration details',
                  'Compliance certifications',
                  'Emergency whitelist support',
                ].map((item, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon
                        sx={{ color: colors.success, fontSize: 18 }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card sx={{ bgcolor: colors.cardBg, boxShadow: 3 }}>
          <CardContent>
            <Typography
              variant='h5'
              sx={{ fontWeight: 600, color: colors.primary, mb: 3 }}
            >
              Frequently Asked Questions
            </Typography>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Why is gcctaxlaws.com being blocked?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Our domain may be categorized as "Newly Observed" by some
                  security vendors. We've submitted recategorization requests
                  and most blocks are resolved within 24-48 hours. IT
                  departments can manually whitelist in the meantime.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Is gcctaxlaws.com safe?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Yes. We have valid SSL certificates (Grade A), no malware, and
                  only host professional legal research content. We welcome
                  security audits and all verification checks show no security
                  issues.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  How long does whitelist approval take?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  This varies by organization but typically 1-3 business days
                  depending on internal IT approval processes. Some
                  organizations can expedite for business-critical applications.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Can individual users access without IT approval?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Users may access via mobile hotspot or personal devices as
                  temporary workarounds, but we recommend proper IT whitelisting
                  for security compliance and to maintain corporate policy
                  adherence.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Do you support VPN access?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Yes, gcctaxlaws.com works with all standard VPN
                  configurations. There are no special requirements or
                  restrictions for VPN-based access.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  )
}

export default EnterpriseAccessPage
